import React from "react";
import {
  EditButton,
  List,
  ShowButton,
  useDataGrid,
} from "@refinedev/mui";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  IResourceComponentsProps,
  useTranslate,
} from "@refinedev/core";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Chip,
  Typography,
  Avatar,
  Stack,
  alpha,
  Card,
  CardContent,
  useMediaQuery,
} from "@mui/material";
import { Route, CheckCircle, Cancel, Navigation } from '@mui/icons-material';
import { useAuth } from "../../contexts/auth-context";

export interface IJourney {
  id: number;
  userId: number;
  user: {
    id: number;
    fullname: string;
    email: string;
    profilePicture?: string;
  };
  originLatitude: number;
  originLongitude: number;
  destinationLatitude: number;
  destinationLongitude: number;
  currentLatitude: number;
  currentLongitude: number;
  hasEnded: boolean;
  createdAt: string;
  updatedAt: string;
  lastHeartbeat?: string;
}

export const JourneyList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const columns = React.useMemo<GridColDef<IJourney>[]>(
    () => [
      {
        field: "user",
        headerName: translate("journeys.fields.user"),
        flex: 1,
        minWidth: isMobile ? 200 : 250,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={row.user?.profilePicture}
              sx={{
                width: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40,
                bgcolor: 'primary.main'
              }}
            >
              {row.user?.fullname?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {row.user?.fullname}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.user?.email}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: "status",
        headerName: translate("journeys.fields.status"),
        width: isMobile ? 120 : 150,
        renderCell: ({ row }) => (
          <Chip
            label={row.hasEnded ? translate("journeys.status.ended") : translate("journeys.status.active")}
            color={row.hasEnded ? "default" : "success"}
            size="small"
            icon={row.hasEnded ? <Cancel /> : <CheckCircle />}
          />
        ),
      },
      {
        field: "createdAt",
        headerName: translate("journeys.fields.startedAt"),
        width: isMobile ? 140 : 180,
        renderCell: ({ value }) => {
          const date = new Date(value);
          return (
            <Stack>
              <Typography variant="body2">
                {date.toLocaleDateString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {date.toLocaleTimeString()}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "lastHeartbeat",
        headerName: translate("journeys.fields.lastUpdate"),
        width: isMobile ? 140 : 180,
        renderCell: ({ value, row }) => {
          if (!value) return <Typography variant="caption">-</Typography>;
          const date = new Date(value);

          return (
            <Typography variant="body2">
              {date.toLocaleTimeString()}
            </Typography>
          );
        },
      },
      {
        field: "actions",
        headerName: translate("common.actions"),
        width: isMobile ? 90 : 120,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <ShowButton size={isMobile ? 'small' : 'medium'} hideText recordItemId={row.id} />
          </Stack>
        ),
      },
    ],
    [translate, isMobile]
  );

  const { dataGridProps } = useDataGrid<IJourney>({
    resource: "journeys",
    initialPageSize: 10,
    pagination: { mode: "server" },
    sorters: {
      initial: [
        {
          field: "createdAt",
          order: "desc",
        },
      ],
    },
  });

  return (
    <Box>
      {/* Header Card */}
      <Card
        sx={{
          mb: 3,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.dark, 0.2)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Navigation sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {translate("journeys.title")}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {translate("journeys.description")}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <List canCreate={false}>
        <Box sx={{ width: "100%", overflow: "auto" }}>
          <DataGrid
            {...dataGridProps}
            columns={columns}
            autoHeight
            density={isMobile ? "compact" : "standard"}
            rowHeight={isMobile ? 56 : 70}
            disableRowSelectionOnClick
            pageSizeOptions={isMobile ? [10, 25] : [10, 25, 50]}
            sx={{
              "& .MuiDataGrid-cell": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              },
            }}
          />
        </Box>
      </List>
    </Box>
  );
};
